import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ContactProvider } from './contexts/ContactContext';
import Login from './components/auth/Login';
import Dashboard from './components/dashboard/Dashboard';
import PlantManagement from './components/plants/PlantManagement';
import CustomerManagement from './components/customers/CustomerManagement';
import WorkerManagement from './components/workers/WorkerManagement';
import WorkerForm from './components/workers/WorkerForm';
import WorkerDetails from './components/workers/WorkerDetails';
import OrderManagement from './components/orders/OrderManagement';
import Deliveries from './components/deliveries/Deliveries';
import PaymentTracking from './components/payments/PaymentTracking';
import Reports from './components/reports/Reports';
import Admin from './components/admin/Admin';
import Layout from './components/layout/Layout';
import SeedingPlanning from './components/seeding/SeedingPlanning';
import Inventory from './components/inventory/Inventory';
import './App.css';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Main App Component
const AppContent: React.FC = () => {
  const { currentUser } = useAuth();

  if (!currentUser) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/plants" element={<PlantManagement />} />
        <Route path="/seeding" element={<SeedingPlanning />} />
        <Route path="/customers" element={<CustomerManagement />} />
        <Route path="/workers" element={<WorkerManagement />} />
        <Route path="/workers/add" element={<WorkerForm />} />
        <Route path="/workers/edit/:id" element={<WorkerForm />} />
        <Route path="/workers/:id" element={<WorkerDetails />} />
        <Route path="/orders" element={<OrderManagement />} />
        <Route path="/deliveries" element={<Deliveries />} />
        <Route path="/payments" element={<PaymentTracking />} />
        <Route path="/inventory" element={<Inventory />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
};

const App: React.FC = () => {
  // Use basename only when deployed to /polyhouse path (check PUBLIC_URL)
  const basename = process.env.PUBLIC_URL?.includes('polyhouse') ? '/polyhouse' : '/';

  return (
    <Router basename={basename}>
      <AuthProvider>
        <ThemeProvider>
          <ContactProvider>
            <div className="App">
              <AppContent />
            </div>
          </ContactProvider>
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
